import React, { useCallback } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import SlideThumbnail from './SlideThumbnail';

import type { ResolvedTheme, Slide, SlideTemplate } from '@/shared/types/slide';

// ---------------------------------------------------------------------------
// SlideThumbnailPanel
//
// Left-hand panel showing miniature thumbnails of each slide. Supports:
//   • Click to select
//   • Drag-to-reorder (via @dnd-kit/sortable)
//   • Add / delete actions
// ---------------------------------------------------------------------------

const THUMBNAIL_WIDTH = 155;
// Always-on custom scrollbar. Styling ::-webkit-scrollbar forces a classic
// (space-consuming) bar exactly this wide, so it reliably reserves this many px
// of the content box (verified in Chromium) — overlay bars can't overlap it.
const SCROLLBAR_WIDTH = 6;
// Left column for the slide number — wide enough for a centered 3-digit number.
const SLIDE_NUMBER_WIDTH = 22;
// Gap between the number column and the thumbnail.
const SLIDE_NUMBER_GAP = 3;
// Left margin before the number column (balances the thumbnail's right gutter).
const SLIDE_NUMBER_MARGIN = 3;
// The panel's own right border. Because box-sizing is border-box it eats into
// the inner width, so it MUST be in the total or the row is 1px short of the
// scrollbar gutter and overflows.
const PANEL_BORDER = 1;
const THUMBNAIL_RIGHT_GUTTER = 11;
// Total width = every horizontal slice, left to right:
//   border | left margin | number | gap | thumbnail | scrollbar gutter
const PANEL_WIDTH =
  PANEL_BORDER +
  SLIDE_NUMBER_MARGIN +
  SLIDE_NUMBER_WIDTH +
  SLIDE_NUMBER_GAP +
  THUMBNAIL_WIDTH +
  THUMBNAIL_RIGHT_GUTTER +
  SCROLLBAR_WIDTH;

interface SlideThumbnailPanelProps {
  slides: Slide[];
  theme: ResolvedTheme;
  currentSlideId: string | null;
  canEdit?: boolean;
  onSelectSlide: (id: string) => void;
  onAddSlide: (template?: SlideTemplate) => void;
  onDeleteSlide: (id: string) => void;
  onReorderSlides: (from: number, to: number) => void;
  onDuplicateSlide?: (id: string) => void;
}

const SlideThumbnailPanel: React.FC<SlideThumbnailPanelProps> = ({
  slides,
  theme,
  currentSlideId,
  canEdit = true,
  onSelectSlide,
  onAddSlide,
  onDeleteSlide,
  onReorderSlides,
  onDuplicateSlide,
}) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const fromIndex = slides.findIndex((s) => s.id === active.id);
      const toIndex = slides.findIndex((s) => s.id === over.id);
      if (fromIndex >= 0 && toIndex >= 0) onReorderSlides(fromIndex, toIndex);
    },
    [slides, onReorderSlides]
  );

  return (
    <Box
      sx={{
        width: PANEL_WIDTH,
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Thumbnail list */}
      <Box
        sx={(t) => ({
          flex: 1,
          overflowY: 'auto',
          py: 1,
          pl: `${SLIDE_NUMBER_MARGIN}px`,
          pr: 0,
          // Reserve the scrollbar's gutter on the right so the layout doesn't
          // shift when the (always-on) bar appears.
          scrollbarGutter: 'stable',
          // Persistent, styled scrollbar — overrides the macOS auto-hide overlay
          // so the bar is always visible when the list is scrollable.
          '&::-webkit-scrollbar': { width: SCROLLBAR_WIDTH },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: `${SCROLLBAR_WIDTH / 2}px`,
            backgroundColor: t.palette.action.disabled,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: t.palette.action.active,
          },
        })}
      >
        <DndContext
          sensors={canEdit ? sensors : undefined}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={slides.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            {slides.map((slide, idx) => (
              <SortableThumb
                key={slide.id}
                slide={slide}
                theme={theme}
                index={idx}
                isActive={slide.id === currentSlideId}
                disabled={!canEdit}
                onSelect={() => onSelectSlide(slide.id)}
                onDelete={canEdit && slides.length > 1 ? () => onDeleteSlide(slide.id) : undefined}
                onDuplicate={
                  canEdit && onDuplicateSlide ? () => onDuplicateSlide(slide.id) : undefined
                }
              />
            ))}
          </SortableContext>
        </DndContext>
      </Box>

      {/* Add button */}
      {canEdit && (
        <Box
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'center',
            py: 1,
          }}
        >
          <Tooltip title="Add slide">
            <IconButton size="small" onClick={() => onAddSlide()}>
              <AddOutlinedIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// SortableThumb
// ---------------------------------------------------------------------------

interface SortableThumbProps {
  slide: Slide;
  theme: ResolvedTheme;
  index: number;
  isActive: boolean;
  disabled?: boolean;
  onSelect: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
}

const SortableThumb: React.FC<SortableThumbProps> = ({
  slide,
  theme,
  index,
  isActive,
  disabled,
  onSelect,
  onDelete,
  onDuplicate,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: slide.id,
    disabled,
  });

  const style: React.CSSProperties = {
    // Translate, not Transform: dnd-kit's post-drop FLIP transform carries scaleX/scaleY
    // that visibly squash rows whose measured box changed during the drag.
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onSelect}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: `${SLIDE_NUMBER_GAP}px`,
        mb: 1,
        cursor: 'pointer',
        '&:hover .thumb-delete, &:hover .thumb-action': { opacity: 1 },
      }}
    >
      {/* Slide number — left column, vertically centered alongside the slide */}
      <Typography
        variant="caption"
        sx={{
          width: SLIDE_NUMBER_WIDTH,
          flexShrink: 0,
          textAlign: 'center',
          color: 'text.secondary',
          fontSize: 11,
          fontWeight: 700,
          userSelect: 'none',
        }}
      >
        {index + 1}
      </Typography>

      {/* Thumbnail — outline and overlay buttons clip to this box */}
      <Box
        sx={{
          position: 'relative',
          width: THUMBNAIL_WIDTH,
          flexShrink: 0,
          outline: isActive ? '2px solid' : '1px solid',
          outlineColor: isActive ? 'primary.main' : 'divider',
          borderRadius: 0.5,
          overflow: 'hidden',
        }}
      >
        {/* Duplicate button overlay */}
        {onDuplicate && (
          <Tooltip title="Duplicate slide">
            <IconButton
              className="thumb-action"
              size="small"
              sx={{
                position: 'absolute',
                top: 0,
                right: onDelete ? 24 : 0,
                zIndex: 2,
                opacity: 0,
                transition: 'opacity 0.15s',
                bgcolor: 'background.paper',
                '&:hover': { bgcolor: 'action.hover' },
              }}
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate();
              }}
            >
              <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Delete button overlay */}
        {onDelete && (
          <IconButton
            className="thumb-delete"
            size="small"
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              zIndex: 2,
              opacity: 0,
              transition: 'opacity 0.15s',
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' },
            }}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <DeleteOutlineOutlinedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}

        {/* Mini canvas — shared, pixel-faithful render path (§7.2) */}
        <SlideThumbnail slide={slide} theme={theme} width={THUMBNAIL_WIDTH} />
      </Box>
    </Box>
  );
};

export default SlideThumbnailPanel;
